import { useId, useRef, useState } from "react";
import type { Vendor1099Report, Vendor1099Vendor } from "@/api/types";
import { formatMoney } from "@/lib/format";
import { taxStatusLabel } from "@/lib/vendorTax";
import { useVendorTaxStore } from "@/lib/vendorTaxStore";
import { VendorTaxDialog } from "./VendorTaxDialog";
import "./MissingTaxInfo.css";

export interface MissingTaxInfoProps {
  report: Vendor1099Report;
  onChanged: () => void;
}

export function MissingTaxInfo({ report, onChanged }: MissingTaxInfoProps) {
  const headingId = useId();
  const store = useVendorTaxStore();
  const [editing, setEditing] = useState<Vendor1099Vendor | null>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const nec = report.vendors.filter((vendor) => vendor.form1099 === "nec");
  const missing = nec.filter((vendor) => vendor.taxStatus === "missing");
  function close(): void {
    setEditing(null);
    queueMicrotask(() => trigger.current?.focus());
  }

  return (
    <>
      {nec.length > 0 && <section className="missing-tax-info" role="region" aria-labelledby={headingId} data-state={missing.length ? "missing" : "complete"}>
        <h3 id={headingId}>Missing tax info</h3>
        {missing.length === 0 ? <p>Every vendor on this list has a tax ID and an address.</p> :
          <ul className="missing-tax-info__list">
            {missing.map((vendor) => {
              const tax = store.rows.get(vendor.vendorId);
              return <li key={vendor.vendorId} data-vendor-id={vendor.vendorId}>
                <span><strong>{vendor.name}</strong> - {tax ? taxStatusLabel(tax) : "Review tax ID and address"} - {formatMoney(vendor.total)}</span>
                <button type="button" className="btn btn--ghost" onClick={(event) => { trigger.current = event.currentTarget; setEditing(vendor); }}>
                  Add tax info for {vendor.name}
                </button>
              </li>;
            })}
          </ul>}
      </section>}
      {report.duplicateTins.length > 0 && <div className="missing-tax-info__duplicates" role="note" aria-label="Duplicate tax IDs">
        <p>Duplicate tax IDs</p>
        <ul>
          {report.duplicateTins.map((group) => <li key={group.vendors.map((vendor) => vendor.vendorId).sort().join("-")}>
            {group.tinType === "ein" ? "**-***" : "***-**-"}{group.tinLast4} is on: {group.vendors.map((vendor) => vendor.name).join(", ")}
          </li>)}
        </ul>
      </div>}
      {editing && <VendorTaxDialog vendor={{ id: editing.vendorId, name: editing.name }}
        initialTax={store.rows.get(editing.vendorId)} onClose={close} onChanged={onChanged} />}
    </>
  );
}
